/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import firebase from 'firebase/compat/app';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';

import { AngularFireDatabase, SnapshotAction } from '@angular/fire/compat/database';
import { Observable } from 'rxjs';
import { TicketIndexLogger } from '../ticket-index-logger.service';
import { ITicketIndexData } from '../ticket-index-data';
import { ticketIndexQuerySnapshot$ } from './query-utils';

/**
 * Abstract superclass of objects that support providing an Observable of SnapshotAction<ITicketIndexData> objects,
 * resulting from some kind of query of this ticket index data:
 *
 *     - indices/ticketsTwo/{projectId}/planWide/{planId}, or
 *     - indices/ticketsTwo/{projectId}/projectWide
 *
 * A subclass may combine two or more Firebase-level queries into a single AbstractIndexQuery's Observable
 * and return the merged Observable via the abstract "getIndexSnapshot$" function.
 */
export abstract class AbstractTicketIndexQuery {
	constructor(protected db: AngularFireDatabase, protected logger: TicketIndexLogger) {}

	/**
	 * Gets the Observable of SnapshotAction<ITicketIndexData> objects for this query
	 * @param projectId The applicable project ID
	 * @param planId The applicable plan ID, or a nullish value to request 'projectWide' data
	 */
	public abstract getIndexSnapshot$(
		projectId: string,
		planId: string
	): Observable<SnapshotAction<ITicketIndexData>>;

	/**
	 * Utility for creating a single, Firebase-level query against a Reference of either:
	 *     - indices/ticketsTwo/{projectId}/planWide/{planId}, or
	 *     - indices/ticketsTwo/{projectId}/projectWide
	 * where the given "queryFunctionToApplyToRef" function applies the query operators to
	 * that Reference.
	 *
	 * @param db Y'know, that thing
	 * @param projectId The applicable project ID
	 * @param planId The applicable plan ID, or a nullish value to request 'projectWide' data
	 * @param queryFunctionToApplyToRef (Optional) Function that applies query operators to the Firebase ref
	 */
	protected simpleQuerySnapshot$(
		projectId: string,
		planId: string,
		queryFunctionToApplyToRef: (ref: firebase.database.Reference) => firebase.database.Query
	): Observable<SnapshotAction<ITicketIndexData>> {
		return ticketIndexQuerySnapshot$(this.db, this.logger, projectId, planId, queryFunctionToApplyToRef);
	}
}
